import { useCallback, useEffect, useState } from 'react'
import { DataTable, ErrorBox, Field, Panel, ScreenHeading, Tag } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { dateTime } from '../../lib/format'

interface OperatorRow {
  id: string
  email: string
  displayName: string
  active: boolean
  createdAt: string
}

interface AuditRow {
  id: number
  at: string
  actorName: string | null
  tenantName: string | null
  action: string
  entityType: string
  entityId: string
  after: unknown
}

export function Operators() {
  const { operator } = useAuth()
  const [rows, setRows] = useState<OperatorRow[] | null>(null)
  const [audit, setAudit] = useState<AuditRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({ email: '', displayName: '', password: '' })
  const [busy, setBusy] = useState(false)

  const reload = useCallback(() => {
    api.get<OperatorRow[]>('/api/platform/operators').then(setRows).catch((e) => setError(errorMessage(e)))
    api.get<AuditRow[]>('/api/platform/audit').then(setAudit).catch(() => {})
  }, [])
  useEffect(() => {
    reload()
  }, [reload])

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      reload()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <ScreenHeading title="Platform operators" />
      {error && <div className="mb-4"><ErrorBox>{error}</ErrorBox></div>}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="New operator" className="lg:col-span-1">
          <div className="space-y-3">
            <Field label="Email"><input className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Display name"><input className="field" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></Field>
            <Field label="Password" hint="at least 8 characters"><input className="field" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
            <button
              type="button"
              className="btn btn-primary w-full"
              disabled={busy || !form.email || !form.displayName || form.password.length < 8}
              onClick={() => act(() => api.post('/api/platform/operators', form)).then(() => setForm({ email: '', displayName: '', password: '' }))}
            >
              Create operator
            </button>
          </div>
        </Panel>
        <Panel className="lg:col-span-2">
          <DataTable
            rows={rows ?? []}
            rowKey={(r) => r.id}
            columns={[
              { key: 'n', header: 'Name', render: (r) => <span className={r.active ? 'text-slate-200' : 'text-slate-500 line-through'}>{r.displayName}{r.id === operator?.id && <span className="ml-1.5 text-xs text-slate-500">you</span>}</span> },
              { key: 'e', header: 'Email', render: (r) => <span className="text-slate-400">{r.email}</span> },
              { key: 'c', header: 'Since', render: (r) => <span className="text-xs text-slate-500">{dateTime(r.createdAt)}</span> },
              { key: 's', header: '', render: (r) => (r.active ? <Tag tone="pos">active</Tag> : <Tag tone="neg">inactive</Tag>) },
              { key: 'a', header: '', align: 'right', render: (r) => (
                r.id === operator?.id ? null : (
                  <button type="button" className={`btn !py-0.5 text-xs ${r.active ? 'btn-danger' : 'btn-pos'}`} disabled={busy} onClick={() => act(() => api.patch(`/api/platform/operators/${r.id}`, { active: !r.active }))}>
                    {r.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                )
              ) },
            ]}
          />
        </Panel>
      </div>
      <div className="mt-4">
        <Panel title="Platform audit" subtitle="Operator actions on tenants, users and operators. Tenant admins also see the actions taken on their own tenant.">
          <DataTable
            rows={audit ?? []}
            rowKey={(r) => String(r.id)}
            maxHeight="24rem"
            columns={[
              { key: 'at', header: 'When', render: (r) => <span className="text-slate-400">{dateTime(r.at)}</span> },
              { key: 'who', header: 'Operator', render: (r) => r.actorName ?? '–' },
              { key: 't', header: 'Tenant', render: (r) => r.tenantName ?? <span className="text-slate-600">–</span> },
              { key: 'a', header: 'Action', render: (r) => <span className="font-mono text-xs text-slate-200">{r.action}</span> },
              { key: 'x', header: 'Detail', render: (r) => <span className="text-xs text-slate-500">{JSON.stringify(r.after ?? null)?.slice(0, 120)}</span> },
            ]}
          />
        </Panel>
      </div>
    </div>
  )
}
